/**
 * The phone half of the web app: register the service worker, subscribe this
 * device to push (server push.ts), and open the session a notification names.
 *
 * iOS delivers web push only to a site added to the Home Screen, so on an
 * iPhone in the browser the control says exactly that instead of a button
 * that cannot work.
 */
import { pushPrefs, pushStatus, pushSubscribe, pushTest, pushUnsubscribe, type PushStatusView } from "./api.js";
import { el, toast } from "./dom.js";

const supported = (): boolean => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIos = (): boolean => /iPhone|iPad|iPod/.test(navigator.userAgent);
const standalone = (): boolean =>
  window.matchMedia?.("(display-mode: standalone)").matches === true || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** Register the worker and route a tapped notification into the app. Never throws. */
export function registerServiceWorker(onNavigate: (url: string) => void): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  navigator.serviceWorker.addEventListener("message", (e: MessageEvent<unknown>) => {
    const d = e.data as { type?: unknown; url?: unknown } | null;
    if (d?.type === "tavsin.navigate" && typeof d.url === "string" && d.url.startsWith("/") && !d.url.startsWith("//")) onNavigate(d.url);
  });
}

/** The session a URL like `/?session=conv_…` names, if any. */
export function sessionFromUrl(url: string): string | null {
  try {
    const id = new URL(url, window.location.origin).searchParams.get("session");
    return id && /^[\w-]{1,64}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

function keyBytes(publicKey: string): Uint8Array {
  const b64 = publicKey.replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/** Ask for permission and subscribe this device. */
async function enable(status: PushStatusView): Promise<boolean> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    toast(permission === "denied" ? "Notifications are blocked for this site — allow them in the browser's settings." : "Notifications were not allowed.", "error");
    return false;
  }
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(status.publicKey) as BufferSource }));
  await pushSubscribe(sub.toJSON());
  return true;
}

async function disable(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await pushUnsubscribe(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe().catch(() => false);
}

/**
 * The "On your phone" block in the notifications popover. Renders a
 * placeholder, then fills itself once the server and the browser answer.
 */
export function phoneSection(): HTMLElement {
  const box = el("div", { class: "phone-push" }, [el("div", { class: "menu-note" }, ["Checking notifications…"])]);
  const fill = async (): Promise<void> => {
    let status: PushStatusView;
    try {
      status = await pushStatus();
    } catch {
      box.replaceChildren(el("div", { class: "menu-note" }, ["Phone notifications are not enabled on this server."]));
      return;
    }
    const head = el("div", { class: "phone-push-head" }, ["On your phone"]);
    if (!supported()) {
      box.replaceChildren(head, el("div", { class: "menu-note" }, [
        isIos() && !standalone()
          ? "On iPhone: tap Share → Add to Home Screen, open Tavsin from there, then turn notifications on here."
          : "This browser cannot receive push notifications.",
      ]));
      return;
    }
    const mine = await currentSubscription().catch(() => null);
    const here = mine !== null && status.devices.some((d) => d.endpoint === mine.endpoint);
    const devices = status.devices.length;
    const summary = el("div", { class: "menu-note" }, [
      devices === 0 ? "Hear when a turn finishes, fails, or needs you — even with the app closed." : `On for ${String(devices)} device${devices === 1 ? "" : "s"}${here ? ", including this one" : ""}.`,
    ]);
    const toggle = (label: string, key: "turns" | "needsYou"): HTMLElement => {
      const input = el("input", { type: "checkbox" });
      input.checked = status.prefs[key];
      input.onchange = () => { void pushPrefs({ [key]: input.checked }).catch(() => toast("Could not save that preference.", "error")); };
      return el("label", { class: "phone-push-pref" }, [input, ` ${label}`]);
    };
    const buttons: HTMLElement[] = [];
    if (!here) {
      const on = el("button", { class: "notif-action" }, ["Notify me on this device"]);
      on.onclick = async () => {
        on.setAttribute("disabled", "true");
        try {
          if (await enable(status)) toast("Notifications are on for this device.", "success");
        } catch (e) {
          toast(e instanceof Error ? e.message : "Could not turn notifications on.", "error");
        }
        void fill();
      };
      buttons.push(on);
    } else {
      const test = el("button", { class: "notif-action" }, ["Send a test"]);
      test.onclick = async () => {
        const r = await pushTest().catch(() => ({ sent: 0, removed: 0 }));
        toast(r.sent > 0 ? `Sent to ${String(r.sent)} device${r.sent === 1 ? "" : "s"}.` : "Nothing was delivered — try turning it off and on.", r.sent > 0 ? "success" : "error");
      };
      const off = el("button", { class: "notif-action" }, ["Turn off here"]);
      off.onclick = async () => { await disable(); void fill(); };
      buttons.push(test, off);
    }
    box.replaceChildren(
      head,
      summary,
      ...(devices > 0 ? [toggle("When a turn finishes or fails", "turns"), toggle("When something needs me", "needsYou")] : []),
      el("div", { class: "phone-push-actions" }, buttons),
    );
  };
  void fill();
  return box;
}
